import { useEffect, useMemo, useRef, useState } from 'react';

import { SeatType } from '@/types';
import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  Drawer,
  FormControlLabel,
  Icon,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from '@/ui';

import CoachSessionParticipants from './CoachSessionParticipants';
import useHideCometChat from './useHideCometChat';

import type { CalendarActionSubject } from '@/features/appointments/calendarAppointmentProjection';
import type { MomentsSelectedPairRescheduleSchedulingContext } from '@/features/appointments/momentsSelectedPairContract';
import type {
  AppointmentScope,
  BrowserRescheduleSchedulingContext,
  CoachAppointmentRescheduleReview,
  NativeRescheduleConflictClass,
  NativeRescheduleSummary,
  RescheduleCandidatePolicyHint,
  Schedule,
} from '@/features/appointments/types';
import type {
  AdminLifecycleCommand,
  PlatformAdminLifecycleGuardOptions,
} from '@/features/appointments/usePlatformAdminAppointmentLifecycleGuard';
import type {
  CoachSessionRescheduleOptions,
  RescheduleSessionResponse,
  SessionRescheduleScope,
  SessionRescheduleSlot,
} from '@/store/api/sessionApi';
import type { Session } from '@/types';
import type { FC } from 'react';

import { MentoCalDatePicker } from '@/components/MentoCal/DatePicker';
import {
  APPOINTMENT_RETRY_BUTTON_LABEL,
  appointmentPendingMessage,
  appointmentRestoredSubmittingMessage,
} from '@/features/appointments/appointmentStatusCopy';
import { NATIVE_COACH_RESCHEDULE_CONTRACT } from '@/features/appointments/browserCapability';
import { legacyAppSelectedPair } from '@/features/appointments/momentsSelectedPairContract';
import { sameInstant } from '@/features/appointments/sameInstant';
import { formatSeriesSummary } from '@/features/appointments/seriesSummary';
import {
  appointmentSeriesCommandFields,
  appointmentUpgradeGuardSpent,
  canonicalAppointmentCommand,
  useAppointmentLifecycleGuard,
} from '@/features/appointments/useAppointmentLifecycleGuard';
import {
  PLATFORM_ADMIN_MANUAL_REVIEW_MESSAGE,
  PLATFORM_ADMIN_RESCHEDULE_REVIEW_REQUIRED_MESSAGE,
  PLATFORM_ADMIN_TERMINAL_HYDRATION_FORBIDDEN_MESSAGE,
  usePlatformAdminAppointmentLifecycleGuard,
} from '@/features/appointments/usePlatformAdminAppointmentLifecycleGuard';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import {
  getAppointmentBrowserCapability,
  shouldReconcileAppointmentWrite,
  useGetCoachAppointmentRescheduleOperationV2Query,
  useGetCoachAppointmentRescheduleSchedulingContextV2Query,
  useLazyGetAppointmentQuery,
  useRescheduleCoachAppointmentV2Mutation,
  useReviewCoachAppointmentRescheduleV2Mutation,
} from '@/store/api/appointmentApi';
import {
  useGetMomentsSelectedPairRescheduleSchedulingContextQuery,
  useReviewMomentsSelectedPairAppointmentRescheduleMutation,
} from '@/store/api/momentsAppointmentApi';
import {
  useAdminChemistryRescheduleSessionMutation,
  useAdminGetChemistryRescheduleOptionsQuery,
  useAdminGetSessionRescheduleOptionsQuery,
  useAdminRescheduleSessionMutation,
  useCoachRescheduleSessionMutation,
  useGetCoachSessionRescheduleOptionsQuery,
} from '@/store/api/sessionApi';
import {
  clearRescheduleMutationId,
  getRescheduleMutationId,
  isNonRetryableClientError,
  rescheduleMutationFingerprint,
} from '@/utils/rescheduleMutation';

interface CoachRescheduleSessionProps {
  actionSubject?: CalendarActionSubject;
  adminCoachId?: string;
  close: () => void;
  lifecycleOptions?: PlatformAdminLifecycleGuardOptions;
  initialStartAt?: string;
  session: Session;
  timeZone?: string | null;
}

interface SlotOption {
  startsAt: string;
  endsAt: string;
  available: boolean;
  conflictClasses?: NativeRescheduleConflictClass[];
  // MEN-4122: the candidate hint's own verdict. Read it rather than deriving
  // blocking-ness from a local class list, so the picker cannot drift from the
  // policy Moments applies.
  selectable?: boolean;
}

interface NativeRescheduleRetryCommand {
  appointmentId: string;
  busyConflictsAcknowledged: boolean;
  expectedSeriesMutationVersion: number | null;
  expectedVersion: number;
  nearTermAcknowledged: boolean;
  reviewDigest: string;
  scope: 'appointment' | 'this_and_following';
  seriesId: string | null;
  startAt: string;
  viewerTimezone: string;
}

const FALLBACK_TIMEZONE = 'UTC';
const RESCHEDULE_DRAWER_OPEN = true;
const MAX_OPERATION_POLL_BACKOFF_MS = 30_000;

const viewerTimezone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || FALLBACK_TIMEZONE;

const formatDateTime = (iso: string, timezone: string) =>
  new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: timezone,
  }).format(new Date(iso));

const formatCadence = (weeks: number, weekday: string) => {
  if (weeks === 1) return `Every ${weekday}`;
  if (weeks === 2) return `Every other ${weekday}`;
  return `Every ${weeks} weeks on ${weekday}`;
};

const formatTime = (iso: string, timezone: string) =>
  new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: timezone,
  }).format(new Date(iso));

const formatDateTimeRange = (
  startAt: string,
  endAt: string,
  timezone: string,
) =>
  `${formatDateTime(startAt, timezone)} - ${formatTime(endAt, timezone)} ${timezone}`;

const formatWeekdayTime = (iso: string, timezone: string) =>
  `${new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    weekday: 'long',
  }).format(new Date(iso))} at ${new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: timezone,
  }).format(new Date(iso))}`;

const formatDate = (date: string) =>
  new Intl.DateTimeFormat('en-US', {
    dateStyle: 'long',
    timeZone: 'UTC',
  }).format(new Date(`${date}T00:00:00Z`));

const nativeTerminalConfirmationText = (
  commandScope: NativeRescheduleRetryCommand['scope'] | undefined,
  schedule: Schedule,
  summary: NativeRescheduleSummary | null,
  fallbackTimezone: string,
): string | null => {
  if (commandScope === 'this_and_following') {
    if (!summary) return null;
    return `${summary.affectedCount} ${summary.affectedCount === 1 ? 'appointment' : 'appointments'} rescheduled from ${formatDate(summary.firstDate)} through ${formatDate(summary.lastDate)} to ${formatCadence(summary.cadenceWeeks, summary.proposedWeekday)} at ${summary.proposedTime} ${summary.timezone}.`;
  }
  return `Appointment rescheduled to ${formatDateTimeRange(
    schedule.startAt,
    schedule.endAt,
    schedule.timezone || fallbackTimezone,
  )}.`;
};

const formatSelectedDayLabel = (date: string) =>
  new Intl.DateTimeFormat('en-US', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
    weekday: 'short',
  }).format(new Date(`${date}T00:00:00Z`));

const dateKey = (iso: string, timezone: string) =>
  new Intl.DateTimeFormat('en-CA', {
    day: '2-digit',
    month: '2-digit',
    timeZone: timezone,
    year: 'numeric',
  }).format(new Date(iso));

const normalizeSlots = (
  slots: Array<
    SessionRescheduleSlot & {
      conflictClasses?: NativeRescheduleConflictClass[];
      selectable?: boolean;
    }
  >,
): SlotOption[] =>
  slots.map((slot) => ({
    available: slot.available,
    conflictClasses: slot.conflictClasses,
    endsAt: slot.end,
    selectable: slot.selectable,
    startsAt: slot.startTime,
  }));

const rescheduleFailureDetail = (error: unknown): string => {
  if (!error || typeof error !== 'object' || !('status' in error)) return '';
  if (typeof error.status === 'number') {
    return ` (HTTP ${String(error.status)})`;
  }
  return typeof error.status === 'string' && error.status.trim()
    ? ` (${error.status})`
    : '';
};

// MEN-4122: coach-facing copy for classes whose wire name does not explain
// itself. `existing_mento_appointment` is the coach's own session already
// holding the slot; it deliberately says so without naming the other party,
// who may be a member this coach cannot otherwise see.
const CONFLICT_CLASS_COPY: Partial<
  Record<NativeRescheduleConflictClass, string>
> = {
  existing_mento_appointment: 'You already have a Mento session at this time.',
};

const conflictClassCopy = (
  conflictClass: NativeRescheduleConflictClass,
): string =>
  CONFLICT_CLASS_COPY[conflictClass] ?? conflictClass.replaceAll('_', ' ');

const slotConflictAnnouncement = (slot: SlotOption): string => {
  if (!slot.conflictClasses?.length) return '';
  const reasons = slot.conflictClasses.map(conflictClassCopy).join(', ');
  // A start the server already refused must not be announced as something an
  // acknowledgement will unlock. Save can never enable for it.
  if (slot.selectable === false) {
    return `Conflict cannot be overridden: ${reasons}`;
  }
  return `Conflict review and acknowledgement required: ${reasons}`;
};

// Every day of the month that contains `date` (YYYY-MM-DD). The coach may pick
// today or a future date; per-slot availability is resolved by the query.
const monthDays = (
  date: string,
  todayIso: string,
): { date: string; enabled: boolean }[] => {
  const [yearStr, monthStr] = date.split('-');
  const year = Number(yearStr);
  const month = Number(monthStr);
  if (!Number.isFinite(year) || !Number.isFinite(month)) return [];
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return Array.from({ length: daysInMonth }, (_, index) => {
    const day = `${yearStr}-${monthStr}-${String(index + 1).padStart(2, '0')}`;
    return { date: day, enabled: day >= todayIso };
  });
};

// Move `date` (YYYY-MM-DD) to the first day of the previous/next month.
const shiftMonth = (date: string, delta: number): string => {
  const [yearStr, monthStr] = date.split('-');
  const year = Number(yearStr);
  const month = Number(monthStr);
  const next = new Date(Date.UTC(year, month - 1 + delta, 1));
  const y = next.getUTCFullYear();
  const m = String(next.getUTCMonth() + 1).padStart(2, '0');
  return `${y}-${m}-01`;
};

const seatLabel = (seatType?: SeatType): string => {
  switch (seatType) {
    case SeatType.standard_leadership:
      return 'Leadership';
    case SeatType.executive:
      return 'Executive';
    default:
      return '';
  }
};

const parseNativeRescheduleRetryCommand = (
  bytes: string | undefined,
): NativeRescheduleRetryCommand | null => {
  if (!bytes) return null;
  try {
    const value = JSON.parse(bytes) as Partial<NativeRescheduleRetryCommand>;
    if (
      typeof value.appointmentId !== 'string' ||
      typeof value.busyConflictsAcknowledged !== 'boolean' ||
      (value.expectedSeriesMutationVersion !== null &&
        !Number.isInteger(value.expectedSeriesMutationVersion)) ||
      !Number.isInteger(value.expectedVersion) ||
      typeof value.nearTermAcknowledged !== 'boolean' ||
      typeof value.reviewDigest !== 'string' ||
      !value.reviewDigest ||
      (value.scope !== 'appointment' && value.scope !== 'this_and_following') ||
      (value.seriesId !== null && typeof value.seriesId !== 'string') ||
      typeof value.startAt !== 'string' ||
      typeof value.viewerTimezone !== 'string'
    ) {
      return null;
    }
    return value as NativeRescheduleRetryCommand;
  } catch {
    return null;
  }
};

interface CandidateRequestTuple {
  appointmentId: string;
  candidateDate: string;
  candidateScope: 'appointment' | 'this_and_following';
}

interface CandidateQueryState {
  currentData?:
    | BrowserRescheduleSchedulingContext
    | MomentsSelectedPairRescheduleSchedulingContext;
  isError?: boolean;
  isFetching?: boolean;
}

const validateNativeRescheduleCandidates = (
  request: CandidateRequestTuple,
  query: CandidateQueryState,
): {
  context:
    | BrowserRescheduleSchedulingContext['context']
    | MomentsSelectedPairRescheduleSchedulingContext['context'];
  hintsByStart: Map<string, RescheduleCandidatePolicyHint>;
} | null => {
  if (query.isFetching || query.isError || !query.currentData) return null;
  const response = query.currentData;
  const target = response.context.rescheduleTarget;
  const day = response.context.availability.days[0];
  if (
    !target ||
    target.id !== request.appointmentId ||
    !target.allowedScopes.includes(request.candidateScope) ||
    response.context.availability.days.length !== 1 ||
    day?.date !== request.candidateDate ||
    day.slots.length > 104 ||
    day.slots.some(
      (slot) =>
        dateKey(slot.startAt, response.context.timezone) !==
        request.candidateDate,
    )
  ) {
    return null;
  }

  const starts = new Set(day.slots.map((slot) => slot.startAt));
  const hintsByStart = new Map<string, RescheduleCandidatePolicyHint>();
  for (const hint of response.candidatePolicyHints) {
    if (
      hint.scope !== request.candidateScope ||
      hintsByStart.has(hint.proposedStartAt)
    ) {
      return null;
    }
    hintsByStart.set(hint.proposedStartAt, hint);
  }
  if (
    starts.size !== day.slots.length ||
    starts.size !== hintsByStart.size ||
    [...starts].some((start) => !hintsByStart.has(start))
  ) {
    return null;
  }
  return { context: response.context, hintsByStart };
};

const confirmationText = (
  result: RescheduleSessionResponse,
  timezone: string,
  scope: SessionRescheduleScope,
): string => {
  const confirmedSession = result.sessions[0];
  if (!confirmedSession) throw new Error('Reschedule response has no sessions');
  const confirmedStart = confirmedSession.startedAt;
  if (scope === 'future_series') {
    const count = result.rescheduledCount;
    const weekdayTime = formatWeekdayTime(confirmedStart, timezone);
    return `${count} future sessions rescheduled to ${weekdayTime}.`;
  }

  return `Session rescheduled to ${formatDateTime(confirmedStart, timezone)}.`;
};

// `selectedDate` is initially derived from `initialTimezone`, but slots are
// bucketed by the resolved schedule `timezone` (data.timezone). When those
// differ, a boundary-straddling session's initial day-key would not match any
// slot's day-key. Re-key the initial date off the resolved timezone once data
// loads so both use the same basis. Manual picks (raw YYYY-MM-DD keys) are
// left untouched.
const useInitialDateTimezoneSync = ({
  datePicked,
  resolvedTimezone,
  selectedDate,
  sessionStart,
  setSelectedDate,
}: {
  datePicked: boolean;
  resolvedTimezone?: string | null;
  selectedDate: string;
  sessionStart: string;
  setSelectedDate: (date: string) => void;
}) => {
  useEffect(() => {
    if (datePicked || !resolvedTimezone) return;
    const resolvedInitialDate = dateKey(sessionStart, resolvedTimezone);
    if (resolvedInitialDate !== selectedDate) {
      setSelectedDate(resolvedInitialDate);
    }
  }, [
    datePicked,
    resolvedTimezone,
    sessionStart,
    selectedDate,
    setSelectedDate,
  ]);
};

const CoachRescheduleSession: FC<CoachRescheduleSessionProps> = ({
  actionSubject,
  adminCoachId,
  close,
  lifecycleOptions,
  initialStartAt,
  session,
  timeZone,
}) => {
  useHideCometChat();
  const { user: currentUser } = useCurrentUser();
  const initialTimezone = timeZone || viewerTimezone();
  // MEN-4122: Ops authority comes from `adminCoachId`, never from the subject
  // tag alone. `platform-admin-appointment` records where the projection came
  // from — `useCalendarWindow` and `ListView` build every native Appointment
  // that way, including the coach's own — so reading it without `adminCoachId`
  // treated a coach as Ops and skipped both scheduling-context queries: the
  // coach one because an "admin" appointment was present, the Ops one because
  // no `adminCoachId` was. `useSessionActions.actionsFor` already pairs the
  // tag with `adminCoachId`; this matches it.
  const adminAppointment =
    adminCoachId && actionSubject?.source === 'platform-admin-appointment'
      ? actionSubject.authoritative
      : null;
  const nativeAppointmentId = adminCoachId
    ? adminAppointment?.id
    : session.sourceAppointmentId;
  const initialDate = dateKey(
    initialStartAt ?? session.startedAt,
    initialTimezone,
  );
  const isAdminChemistry = Boolean(adminCoachId && session.isChemistryCall);
  const isAdminCoachCollision = Boolean(
    !adminAppointment &&
    adminCoachId &&
    !session.isChemistryCall &&
    currentUser.id === adminCoachId,
  );
  const isAdminMemberCollision = Boolean(
    adminAppointment && currentUser.id === adminAppointment.member.id,
  );
  const [scope, setScope] = useState<SessionRescheduleScope>('single');
  const [selectedDate, setSelectedDate] = useState(initialDate);
  const coachQuery = useGetCoachSessionRescheduleOptionsQuery(
    {
      date: selectedDate,
      id: session.id,
      scope,
      viewerTimezone: initialTimezone,
    },
    { skip: Boolean(adminCoachId) || Boolean(nativeAppointmentId) },
  );
  const adminQuery = useAdminGetSessionRescheduleOptionsQuery(
    {
      coachId: adminCoachId ?? '',
      date: selectedDate,
      id: session.id,
      memberId: session.userId,
      scope,
      viewerTimezone: initialTimezone,
    },
    {
      skip:
        !adminCoachId ||
        session.isChemistryCall ||
        Boolean(nativeAppointmentId),
    },
  );
  const chemistryQuery = useAdminGetChemistryRescheduleOptionsQuery(
    {
      coachId: adminCoachId ?? '',
      id: session.id,
      memberId: session.userId,
      viewerTimezone: initialTimezone,
      visibleWeeks: 3,
    },
    { skip: !isAdminChemistry || Boolean(nativeAppointmentId) },
  );
  const coachReviewScope: AppointmentScope =
    scope === 'future_series' ? 'this_and_following' : 'appointment';
  const candidateRequest = {
    appointmentId: nativeAppointmentId ?? '',
    candidateDate: selectedDate,
    candidateScope: coachReviewScope,
  } as const;
  const nativeQuery = useGetCoachAppointmentRescheduleSchedulingContextV2Query(
    {
      ...candidateRequest,
      kind: session.isChemistryCall ? 'chemistry' : 'coaching',
      userId: currentUser.id,
      viewerTimezone: initialTimezone,
      visibleWeeks: 3,
    },
    {
      skip: !nativeAppointmentId || Boolean(adminAppointment),
    },
  );
  const adminSelectedPair = useMemo(
    () =>
      legacyAppSelectedPair(
        adminCoachId ?? '',
        adminAppointment?.member.id ?? '',
      ),
    [adminAppointment?.member.id, adminCoachId],
  );
  const adminNativeQuery =
    useGetMomentsSelectedPairRescheduleSchedulingContextQuery(
      {
        ...adminSelectedPair,
        ...candidateRequest,
        kind: adminAppointment?.kind ?? 'coaching',
        viewerTimezone: initialTimezone,
        visibleWeeks: 3,
      },
      { skip: !adminAppointment || !adminCoachId },
    );
  const selectedNativeQuery = adminAppointment ? adminNativeQuery : nativeQuery;
  const validatedCandidates = validateNativeRescheduleCandidates(
    candidateRequest,
    selectedNativeQuery,
  );
  const candidateTarget = validatedCandidates?.context.rescheduleTarget;
  const candidateTargetMatches =
    !adminAppointment ||
    Boolean(
      candidateTarget &&
      candidateTarget.version === adminAppointment.version &&
      candidateTarget.seriesId === adminAppointment.seriesId &&
      candidateTarget.seriesMutationVersion ===
        adminAppointment.seriesMutationVersion,
    );
  const nativeCandidateValidationError = Boolean(
    nativeAppointmentId &&
    selectedNativeQuery.currentData &&
    !selectedNativeQuery.isFetching &&
    !selectedNativeQuery.isError &&
    (!validatedCandidates || !candidateTargetMatches),
  );
  const nativeContext = adminAppointment
    ? undefined
    : validatedCandidates?.context;
  const selectedNativeContext = candidateTargetMatches
    ? validatedCandidates?.context
    : undefined;
  const chemistryData = useMemo<CoachSessionRescheduleOptions | undefined>(
    () =>
      chemistryQuery.data
        ? {
            sessionSeries: null,
            slots: chemistryQuery.data.days.flatMap((day) =>
              day.slots.map((slot) => ({
                available: day.enabled,
                end: slot.endTime,
                label: slot.label,
                startTime: slot.startTime,
              })),
            ),
            timezone: chemistryQuery.data.timezone,
          }
        : undefined,
    [chemistryQuery.data],
  );
  let { data }: { data?: CoachSessionRescheduleOptions } = coachQuery;
  let { isError, isFetching, isLoading } = coachQuery;
  if (adminCoachId) {
    const selectedAdminQuery = session.isChemistryCall
      ? chemistryQuery
      : adminQuery;
    data = session.isChemistryCall ? chemistryData : adminQuery.data;
    isError = selectedAdminQuery.isError;
    isFetching = selectedAdminQuery.isFetching;
    isLoading = selectedAdminQuery.isLoading;
  }
  if (nativeAppointmentId) {
    data = selectedNativeContext
      ? {
          sessionSeries: selectedNativeContext.activeSeries
            ? {
                futureSessionCount:
                  selectedNativeContext.activeSeries.remainingAppointmentCount,
                intervalWeeks: selectedNativeContext.activeSeries.cadenceWeeks,
                startsOn: dateKey(
                  selectedNativeContext.activeSeries.nextSchedule.startAt,
                  selectedNativeContext.timezone,
                ),
                // MEN-4122: the Ops contract requires `affectedEndAt`, so the
                // series tail is known there and the summary can show its full
                // range. The browser contract has no equivalent field, so the
                // coach summary stays cadence-only rather than projecting a
                // date the server never asserted.
                endsOn:
                  'affectedEndAt' in selectedNativeContext.activeSeries
                    ? dateKey(
                        selectedNativeContext.activeSeries.affectedEndAt,
                        selectedNativeContext.timezone,
                      )
                    : null,
              }
            : null,
          slots: selectedNativeContext.availability.days.flatMap((day) =>
            day.slots.map((slot) => ({
              // Render every supplied candidate, including conflicts. The hint
              // explains its verdict; authoritative review gates saving.
              available: true,
              conflictClasses: validatedCandidates?.hintsByStart.get(
                slot.startAt,
              )?.conflictClasses,
              end: slot.endAt,
              label: slot.label,
              selectable: validatedCandidates?.hintsByStart.get(slot.startAt)
                ?.selectable,
              startTime: slot.startAt,
            })),
          ),
          timezone: selectedNativeContext.timezone,
        }
      : undefined;
    if (!data && nativeCandidateValidationError) {
      data = { sessionSeries: null, slots: [], timezone: initialTimezone };
    }
    isError = selectedNativeQuery.isError || nativeCandidateValidationError;
    isFetching = selectedNativeQuery.isFetching;
    isLoading = selectedNativeQuery.isLoading;
  }
  const [coachRescheduleSession, coachMutation] =
    useCoachRescheduleSessionMutation();
  const [adminRescheduleSession, adminMutation] =
    useAdminRescheduleSessionMutation();
  const [adminChemistryRescheduleSession, chemistryMutation] =
    useAdminChemistryRescheduleSessionMutation();
  const [rescheduleCoachAppointmentV2, appointmentMutation] =
    useRescheduleCoachAppointmentV2Mutation();
  const [reviewCoachAppointmentRescheduleV2, reviewMutation] =
    useReviewCoachAppointmentRescheduleV2Mutation();
  const [reviewAdminAppointmentReschedule, adminReviewMutation] =
    useReviewMomentsSelectedPairAppointmentRescheduleMutation();
  let isSaving = nativeAppointmentId
    ? appointmentMutation.isLoading
    : coachMutation.isLoading;
  if (adminCoachId && !nativeAppointmentId) {
    isSaving = session.isChemistryCall
      ? chemistryMutation.isLoading
      : adminMutation.isLoading;
  }
  const timezone = data?.timezone ?? initialTimezone;
  const todayIso = dateKey(new Date().toISOString(), timezone);
  const currentStart = session.startedAt;
  const currentEnd = session.endedAt;
  // MEN-4122: the cadence weekday and time must come from the series' own next
  // occurrence, not from the Session row the coach happened to click. The
  // scheduling context only guarantees the target belongs to the series, so a
  // later occurrence or a lagging projection would otherwise print a weekday
  // the series does not use. Legacy responses carry no series schedule and
  // keep using the displayed occurrence.
  const seriesAnchor = nativeAppointmentId
    ? selectedNativeContext?.activeSeries?.nextSchedule
    : undefined;
  const seriesSummary = data?.sessionSeries
    ? formatSeriesSummary(
        seriesAnchor?.startAt ?? currentStart,
        seriesAnchor?.endAt ?? currentEnd,
        timezone,
        {
          ...data.sessionSeries,
          // MEN-4306: the count is named for the reschedule contract, the
          // summary names it for every caller. Without the mapping a series
          // that carries a window but no cadence still printed the generic
          // label, because the helper had no count to put in front of it.
          occurrenceCount: data.sessionSeries.futureSessionCount,
        },
      )
    : null;
  const nativeAllowsFutureSeries = Boolean(
    selectedNativeContext?.rescheduleTarget?.allowedScopes.includes(
      'this_and_following',
    ),
  );
  const canApplyToFutureSeries = nativeAppointmentId
    ? Boolean(data?.sessionSeries) && nativeAllowsFutureSeries
    : Boolean(data?.sessionSeries);
  const slots = useMemo(() => normalizeSlots(data?.slots ?? []), [data?.slots]);
  const [selectedTimeInput, setSelectedTimeInput] = useState(
    initialStartAt ?? '',
  );
  const selectedTime =
    initialStartAt && selectedTimeInput === initialStartAt
      ? (slots.find((slot) => sameInstant(slot.startsAt, initialStartAt))
          ?.startsAt ?? '')
      : selectedTimeInput;
  const [authoritativeReview, setAuthoritativeReview] =
    useState<CoachAppointmentRescheduleReview | null>(null);
  const [completedReviewIdentity, setCompletedReviewIdentity] = useState('');
  const [busyConflictsAcknowledged, setBusyConflictsAcknowledged] =
    useState(false);
  const [nearTermAcknowledged, setNearTermAcknowledged] = useState(false);
  const [legacyUnavailableAcknowledged, setLegacyUnavailableAcknowledged] =
    useState(false);
  const reviewGenerationRef = useRef(0);
  const [timeMenuAnchor, setTimeMenuAnchor] = useState<HTMLElement | null>(
    null,
  );
  const [confirmation, setConfirmation] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [upgradeRequired, setUpgradeRequired] = useState(false);
  const [operationReviewRequired, setOperationReviewRequired] = useState(false);
  const [pendingOperationId, setPendingOperationId] = useState<string | null>(
    null,
  );
  const [
    pendingOperationRetryAfterSeconds,
    setPendingOperationRetryAfterSeconds,
  ] = useState<number | null>(null);
  const [operationPollFailureCount, setOperationPollFailureCount] = useState(0);
  const [submitInFlight, setSubmitInFlight] = useState(false);
  const submitInFlightRef = useRef(false);
  const releaseSubmit = () => {
    submitInFlightRef.current = false;
    setSubmitInFlight(false);
  };
  const restoredPendingAppliedRef = useRef(false);
  const [getAppointment] = useLazyGetAppointmentQuery();
  const appointmentCapability = getAppointmentBrowserCapability();
  const lifecycle = useAppointmentLifecycleGuard({
    capability: appointmentCapability,
    displayed: {
      id: nativeAppointmentId,
      seriesId:
        session.sourceAppointmentVersion === undefined
          ? nativeContext?.rescheduleTarget?.seriesId
          : session.sourceAppointmentSeriesId,
      seriesMutationVersion:
        session.sourceAppointmentVersion === undefined
          ? nativeContext?.rescheduleTarget?.seriesMutationVersion
          : session.sourceAppointmentSeriesMutationVersion,
      version:
        session.sourceAppointmentVersion ??
        nativeContext?.rescheduleTarget?.version,
    },
    ownerKey:
      nativeAppointmentId && !adminAppointment
        ? `coach-reschedule:${adminCoachId ?? 'coach'}:${session.id}:${nativeAppointmentId}`
        : undefined,
    protocolMarker: NATIVE_COACH_RESCHEDULE_CONTRACT,
    readAppointment: async () => {
      const request = getAppointment({
        appointmentId: nativeAppointmentId ?? '',
      });
      return request.unwrap().finally(() => request.unsubscribe());
    },
  });
  const [adminIdempotencyKey, setAdminIdempotencyKey] = useState(() =>
    window.crypto.randomUUID(),
  );
  const adminCommand: AdminLifecycleCommand = {
    appointmentId: adminAppointment?.id ?? '',
    coachId: adminAppointment?.coach.id ?? adminCoachId ?? '',
    command: 'reschedule',
    idempotencyKey: adminIdempotencyKey,
    kind: adminAppointment?.kind ?? 'coaching',
    memberId: adminAppointment?.member.id ?? '',
    payload: {
      expectedSeriesMutationVersion:
        adminAppointment?.seriesId === null
          ? null
          : (adminAppointment?.seriesMutationVersion ?? null),
      expectedVersion: adminAppointment?.version ?? 0,
      busyConflictsAcknowledged,
      nearTermAcknowledged,
      reviewDigest: authoritativeReview?.reviewDigest ?? '',
      seriesId: adminAppointment?.seriesId ?? null,
      scope: scope === 'future_series' ? 'this_and_following' : 'appointment',
      startAt: selectedTime,
      viewerTimezone: timezone,
    },
    presentation: {
      memberEmail:
        selectedNativeContext && 'member' in selectedNativeContext
          ? selectedNativeContext.member.email
          : session.user.email,
    },
  };
  const adminLifecycle = usePlatformAdminAppointmentLifecycleGuard(
    adminCommand,
    {
      ...lifecycleOptions,
      enabled: Boolean(adminAppointment),
    },
  );
  const adminNonterminal =
    adminLifecycle.record?.phase === 'submitting' ||
    adminLifecycle.record?.phase === 'ambiguous' ||
    adminLifecycle.record?.phase === 'pending';
  const adminRejected = adminLifecycle.record?.phase === 'rejected';
  const adminManualReview =
    adminLifecycle.record?.phase === 'needs_reconciliation';
  const adminCommandFrozen =
    adminNonterminal || adminManualReview || adminRejected;
  const adminAmbiguous = adminLifecycle.record?.phase === 'ambiguous';
  const canDisplayFutureSeries =
    canApplyToFutureSeries ||
    Boolean(
      adminLifecycle.record &&
      adminLifecycle.command.command === 'reschedule' &&
      adminLifecycle.command.payload.scope === 'this_and_following',
    );
  useEffect(() => {
    if (
      !adminLifecycle.record ||
      adminLifecycle.command.command !== 'reschedule'
    )
      return;
    const stored = adminLifecycle.command.payload;
    setScope(
      stored.scope === 'this_and_following' ? 'future_series' : 'single',
    );
    setSelectedDate(dateKey(stored.startAt, stored.viewerTimezone));
    setSelectedTimeInput(stored.startAt);
    setTimeMenuAnchor(null);
  }, [adminLifecycle.command, adminLifecycle.record]);
  const {
    clearOwnedOperation,
    consumeConfirmedReceipt,
    currentCommand,
    recordConfirmed,
    recordReconciliationRequired,
    rereadAfterRejection,
  } = lifecycle;
  const restoredCommandBytes =
    lifecycle.restoredSubmitting?.bytes ?? lifecycle.restoredPending?.bytes;
  const restoredRetryCommand = useMemo(
    () => parseNativeRescheduleRetryCommand(restoredCommandBytes),
    [restoredCommandBytes],
  );
  // The coach has manually navigated the picker; picker dates are raw
  // YYYY-MM-DD keys and must not be re-keyed against a timezone.
  const datePickedRef = useRef(false);

  useInitialDateTimezoneSync({
    datePicked: datePickedRef.current,
    resolvedTimezone: data?.timezone,
    selectedDate,
    sessionStart: initialStartAt ?? session.startedAt,
    setSelectedDate,
  });

  useEffect(() => {
    if (
      !restoredRetryCommand ||
      restoredRetryCommand.appointmentId !== nativeAppointmentId
    ) {
      return;
    }
    datePickedRef.current = true;
    setScope(
      restoredRetryCommand.scope === 'this_and_following'
        ? 'future_series'
        : 'single',
    );
    setSelectedDate(
      dateKey(
        restoredRetryCommand.startAt,
        restoredRetryCommand.viewerTimezone,
      ),
    );
    setSelectedTimeInput(restoredRetryCommand.startAt);
    setBusyConflictsAcknowledged(
      restoredRetryCommand.busyConflictsAcknowledged,
    );
    setNearTermAcknowledged(restoredRetryCommand.nearTermAcknowledged);
  }, [nativeAppointmentId, restoredRetryCommand]);

  const coachReviewTarget = selectedNativeContext?.rescheduleTarget;
  const currentReviewIdentity =
    nativeAppointmentId &&
    coachReviewTarget &&
    selectedTime &&
    selectedNativeContext.availability.days.some((day) =>
      day.slots.some((slot) => slot.startAt === selectedTime),
    )
      ? JSON.stringify([
          nativeAppointmentId,
          coachReviewTarget.seriesId,
          coachReviewTarget.version,
          coachReviewTarget.seriesMutationVersion,
          coachReviewScope,
          selectedTime,
          timezone,
        ])
      : '';
  const restoredRetryMatchesControls = Boolean(
    restoredRetryCommand &&
    restoredRetryCommand.appointmentId === nativeAppointmentId &&
    restoredRetryCommand.scope === coachReviewScope &&
    restoredRetryCommand.startAt === selectedTime,
  );
  const exactNativeRetry = Boolean(
    lifecycle.restoredSubmitting && restoredRetryMatchesControls,
  );

  const exactAdminRetry = Boolean(
    adminAmbiguous &&
    adminLifecycle.command.command === 'reschedule' &&
    adminLifecycle.command.payload.reviewDigest &&
    typeof adminLifecycle.command.payload.busyConflictsAcknowledged ===
      'boolean' &&
    typeof adminLifecycle.command.payload.nearTermAcknowledged === 'boolean',
  );
  const isAdminReschedule = Boolean(adminAppointment);
  const reviewTargetSeriesId = coachReviewTarget?.seriesId;
  const reviewTargetVersion = coachReviewTarget?.version;
  const reviewTargetSeriesMutationVersion =
    coachReviewTarget?.seriesMutationVersion;
  const selectedAdminReview = isAdminReschedule
    ? reviewAdminAppointmentReschedule
    : null;
  const selectedCoachReview = isAdminReschedule
    ? null
    : reviewCoachAppointmentRescheduleV2;
  useEffect(() => {
    const generation = reviewGenerationRef.current + 1;
    reviewGenerationRef.current = generation;
    setAuthoritativeReview(null);
    setCompletedReviewIdentity('');
    setBusyConflictsAcknowledged(
      restoredRetryMatchesControls
        ? restoredRetryCommand!.busyConflictsAcknowledged
        : false,
    );
    setNearTermAcknowledged(
      restoredRetryMatchesControls
        ? restoredRetryCommand!.nearTermAcknowledged
        : false,
    );
    if (
      !currentReviewIdentity ||
      reviewTargetSeriesId === undefined ||
      reviewTargetVersion === undefined ||
      reviewTargetSeriesMutationVersion === undefined ||
      !nativeAppointmentId ||
      lifecycle.restoredSubmitting ||
      adminCommandFrozen ||
      lifecycle.restoredPending ||
      pendingOperationId ||
      confirmation ||
      operationReviewRequired
    ) {
      return undefined;
    }

    const commonReview = {
      appointmentId: nativeAppointmentId,
      expectedSeriesMutationVersion:
        isAdminReschedule && reviewTargetSeriesId === null
          ? null
          : reviewTargetSeriesMutationVersion,
      expectedVersion: reviewTargetVersion,
      scope: coachReviewScope,
      seriesId: reviewTargetSeriesId,
      viewerTimezone: timezone,
    };
    const request = selectedAdminReview
      ? selectedAdminReview({
          ...adminSelectedPair,
          ...commonReview,
          startAt: selectedTime,
        })
      : selectedCoachReview!({
          ...commonReview,
          expectedSeriesMutationVersion: reviewTargetSeriesMutationVersion,
          proposedStartAt: selectedTime,
        });
    request
      .unwrap()
      .then((review) => {
        if (reviewGenerationRef.current !== generation) return;
        if (
          review.scope !== coachReviewScope ||
          review.seriesId !== reviewTargetSeriesId ||
          !sameInstant(review.proposedSchedule.startAt, selectedTime)
        ) {
          setErrorMessage(
            'The reschedule review did not match the selected schedule. Choose a time and try again.',
          );
          return;
        }
        setAuthoritativeReview(review);
        setCompletedReviewIdentity(currentReviewIdentity);
      })
      .catch(() => {
        if (reviewGenerationRef.current !== generation) return;
        setErrorMessage(
          'Unable to review this reschedule. Choose a time and try again.',
        );
      });
    return () => {
      if ('abort' in request && typeof request.abort === 'function') {
        request.abort();
      }
    };
  }, [
    adminCommandFrozen,
    adminSelectedPair,
    coachReviewScope,
    confirmation,
    currentReviewIdentity,
    isAdminReschedule,
    lifecycle.restoredPending,
    lifecycle.restoredSubmitting,
    nativeAppointmentId,
    operationReviewRequired,
    pendingOperationId,
    reviewTargetSeriesId,
    reviewTargetSeriesMutationVersion,
    reviewTargetVersion,
    restoredRetryCommand,
    restoredRetryMatchesControls,
    selectedAdminReview,
    selectedCoachReview,
    selectedTime,
    timezone,
  ]);

  const operationStatus = useGetCoachAppointmentRescheduleOperationV2Query(
    {
      appointmentId: nativeAppointmentId ?? '',
      operationId: pendingOperationId ?? '',
      userId: currentUser.id,
    },
    {
      pollingInterval: upgradeRequired
        ? 0
        : Math.min(
            MAX_OPERATION_POLL_BACKOFF_MS,
            Math.max(1000, (pendingOperationRetryAfterSeconds ?? 5) * 1000) *
              2 ** operationPollFailureCount,
          ),
      skip:
        !currentUser.id ||
        !pendingOperationId ||
        upgradeRequired ||
        operationReviewRequired,
    },
  );
  const requestClose = () => {
    if (
      pendingOperationId ||
      lifecycle.isReading ||
      submitInFlight ||
      isSaving ||
      adminLifecycle.record?.phase === 'submitting' ||
      adminLifecycle.record?.phase === 'ambiguous' ||
      adminLifecycle.record?.phase === 'pending'
    )
      return;
    close();
  };
  const startNewAdminAttempt = () => {
    adminLifecycle
      .clear()
      .then((cleared) => {
        if (!cleared) return;
        setAdminIdempotencyKey(window.crypto.randomUUID());
        setErrorMessage(null);
      })
      .catch(() => undefined);
  };

  useEffect(() => {
    if (lifecycle.restoredPending && !restoredPendingAppliedRef.current) {
      restoredPendingAppliedRef.current = true;
      setPendingOperationId(lifecycle.restoredPending.operationId);
      setPendingOperationRetryAfterSeconds(
        lifecycle.restoredPending.retryAfterSeconds,
      );
    }
  }, [lifecycle.restoredPending]);

  useEffect(() => {
    if (!lifecycle.restoredReconciliation) return;
    setOperationReviewRequired(true);
    setErrorMessage(
      `Operation ${lifecycle.restoredReconciliation.operationId} requires Ops reconciliation before another change.`,
    );
  }, [lifecycle.restoredReconciliation]);

  useEffect(() => {
    if (!lifecycle.confirmedReceipt) return;
    if (!consumeConfirmedReceipt()) return;
    close();
  }, [close, consumeConfirmedReceipt, lifecycle.confirmedReceipt]);

  useEffect(() => {
    if (appointmentUpgradeGuardSpent(operationStatus.error)) {
      setUpgradeRequired(true);
    }
  }, [operationStatus.error]);

  useEffect(() => {
    if (!pendingOperationId) {
      setOperationPollFailureCount(0);
      return;
    }
    if (appointmentUpgradeGuardSpent(operationStatus.error)) return;
    if (operationStatus.error) {
      setOperationPollFailureCount((failures) => Math.min(failures + 1, 5));
      return;
    }
    if (operationStatus.currentData) {
      setOperationPollFailureCount(0);
    }
  }, [operationStatus.currentData, operationStatus.error, pendingOperationId]);

  useEffect(() => {
    const operation = operationStatus.currentData;
    if (!operation || operation.id !== pendingOperationId) return;
    if (
      operation.status === 'pending' ||
      operation.status === 'recovery_required'
    ) {
      setPendingOperationRetryAfterSeconds(operation.retryAfterSeconds ?? null);
    }
    if (operation.status === 'confirmed') {
      if (
        !operation.appointments.some(
          ({ id }) => id === (nativeAppointmentId ?? ''),
        )
      ) {
        setOperationReviewRequired(true);
        setPendingOperationId(null);
        setPendingOperationRetryAfterSeconds(null);
        releaseSubmit();
        setErrorMessage(
          'The confirmed operation does not match this appointment. The reschedule remains saved for review.',
        );
        return;
      }
      const confirmedAppointment =
        operation.appointments.find(({ id }) => id === nativeAppointmentId) ??
        operation.appointments[0]!;
      const confirmedCommand =
        parseNativeRescheduleRetryCommand(currentCommand()?.bytes) ??
        restoredRetryCommand;
      const terminalConfirmation = nativeTerminalConfirmationText(
        confirmedCommand?.scope,
        confirmedAppointment.currentSchedule,
        operation.summary,
        timezone,
      );
      if (!terminalConfirmation) {
        setOperationReviewRequired(true);
        setPendingOperationId(null);
        setPendingOperationRetryAfterSeconds(null);
        releaseSubmit();
        setErrorMessage(
          'The confirmed series operation did not include its authoritative summary. The reschedule remains saved for review.',
        );
        return;
      }
      const handled = recordConfirmed();
      setPendingOperationId(null);
      setPendingOperationRetryAfterSeconds(null);
      releaseSubmit();
      if (!handled) return;
      setConfirmation(terminalConfirmation);
    }
    if (operation.status === 'rejected') {
      setPendingOperationId(null);
      setPendingOperationRetryAfterSeconds(null);
      releaseSubmit();
      const command = currentCommand();
      if (command) {
        rereadAfterRejection({
          ...command,
          operationId: operation.id,
        }).catch(() => undefined);
      }
      setErrorMessage(
        operation.reason ??
          'Unable to reschedule this session. Please try again.',
      );
    }
    if (operation.status === 'reconciliation_required') {
      setOperationReviewRequired(true);
      setPendingOperationId(null);
      setPendingOperationRetryAfterSeconds(null);
      releaseSubmit();
      setErrorMessage(
        operation.reason ??
          'This reschedule requires Ops reconciliation before another change.',
      );
    }
  }, [
    operationStatus.currentData,
    pendingOperationId,
    rereadAfterRejection,
    recordConfirmed,
    restoredRetryCommand,
    currentCommand,
    nativeAppointmentId,
    timezone,
  ]);

  useEffect(() => {
    if (!adminAppointment || !adminLifecycle.result) return;
    if (adminLifecycle.result.status === 'confirmed') {
      const confirmedStart =
        adminLifecycle.result.appointments.find(
          ({ id }) => id === adminAppointment.id,
        )?.currentSchedule.startAt ?? selectedTime;
      if (confirmedStart) {
        setConfirmation(
          `Appointment rescheduled to ${formatDateTime(confirmedStart, timezone)}.`,
        );
      }
    } else if (adminLifecycle.result.status === 'rejected') {
      setErrorMessage(
        adminLifecycle.result.rejection?.message ??
          'Unable to reschedule this session. Please try again.',
      );
    }
  }, [adminAppointment, adminLifecycle.result, selectedTime, timezone]);

  const pickerDays = useMemo(
    () =>
      isAdminChemistry && !nativeAppointmentId
        ? (chemistryQuery.data?.days.map(({ date, enabled }) => ({
            date,
            enabled,
          })) ?? [])
        : monthDays(selectedDate, todayIso),
    [
      chemistryQuery.data?.days,
      isAdminChemistry,
      nativeAppointmentId,
      selectedNativeContext?.availability.days,
      selectedDate,
      todayIso,
    ],
  );
  const isCurrentOrEarlierMonth =
    selectedDate.slice(0, 7) <= todayIso.slice(0, 7);

  const visibleSlots = slots.filter(
    (slot) => dateKey(slot.startsAt, timezone) === selectedDate,
  );
  const selectedSlot = visibleSlots.find(
    (slot) => slot.startsAt === selectedTime,
  );
  const unavailableSlot =
    selectedSlot?.available === false ? selectedSlot : null;
  const millisecondsUntilSession =
    new Date(currentStart).getTime() - Date.now();
  const isWithin48Hours =
    !nativeAppointmentId &&
    millisecondsUntilSession > 0 &&
    millisecondsUntilSession <= 48 * 60 * 60 * 1000;
  useEffect(() => {
    setLegacyUnavailableAcknowledged(false);
  }, [selectedDate, selectedTime, scope, session.id]);
  const reviewMatchesControls =
    Boolean(authoritativeReview) &&
    completedReviewIdentity === currentReviewIdentity;
  // MEN-4122: one acknowledgement covers every overrideable conflict on the
  // review. Reading `disposition` rather than matching `calendar_busy` keeps this
  // gate aligned with the authoritative policy, so a class Moments later makes
  // overrideable cannot reach Save without being acknowledged here. The command
  // field below keeps the name `busyConflictsAcknowledged` because it is part of
  // the canonical command bytes and the durable restored-retry record.
  //
  // MEN-4122: and never offer it for a review that is not selectable, or that
  // carries any non-selectable conflict. Checking the box could not enable Save
  // -- `nativeReviewAllowsSubmit` requires `selectable` -- so rendering it
  // invites the coach to override something that can never be overridden.
  // Both conditions come from the server's own verdict rather than a local list
  // of blocking classes, so this cannot drift from Moments' policy.
  const overrideAcknowledgementRequired = Boolean(
    authoritativeReview?.selectable === true &&
    authoritativeReview.conflicts.every((conflict) => conflict.selectable) &&
    authoritativeReview.conflicts.some(
      (conflict) => conflict.disposition === 'overrideable',
    ),
  );
  const nearTermAcknowledgementRequired = Boolean(
    authoritativeReview?.timing.acknowledgementRequired,
  );
  const nativeReviewAllowsSubmit =
    exactNativeRetry ||
    exactAdminRetry ||
    (reviewMatchesControls &&
      authoritativeReview?.selectable === true &&
      selectedSlot?.selectable === true &&
      authoritativeReview.conflicts.every(
        (conflict) =>
          conflict.selectable && conflict.disposition !== 'blocking',
      ) &&
      (!overrideAcknowledgementRequired || busyConflictsAcknowledged) &&
      (!nearTermAcknowledgementRequired || nearTermAcknowledged));
  const canSubmit =
    (Boolean(selectedSlot) || exactAdminRetry || exactNativeRetry) &&
    !isAdminCoachCollision &&
    !isAdminMemberCollision &&
    (!isError || exactNativeRetry || exactAdminRetry) &&
    !isSaving &&
    !submitInFlight &&
    (!isFetching || exactNativeRetry || exactAdminRetry) &&
    !operationReviewRequired &&
    !adminLifecycle.requiresRescheduleReview &&
    !upgradeRequired &&
    !pendingOperationId &&
    (!nativeAppointmentId || nativeReviewAllowsSubmit) &&
    (!unavailableSlot ||
      Boolean(nativeAppointmentId) ||
      legacyUnavailableAcknowledged);
  const occurrenceSummary = `${formatDateTime(
    currentStart,
    timezone,
  )} - ${formatTime(currentEnd, timezone)}`;
  let displayedSummary = occurrenceSummary;
  if (scope === 'future_series') {
    displayedSummary = seriesSummary ?? 'All future sessions in this series.';
  }

  const renderTimeOptions = () => {
    if (isFetching) {
      return <CircularProgress data-testid="coach-reschedule-time-loading" />;
    }
    if (isError) {
      return (
        <Alert data-testid="coach-reschedule-time-error" severity="error">
          Unable to load reschedule options. Please try again.
        </Alert>
      );
    }
    if (visibleSlots.length === 0) {
      return (
        <Alert severity="info">
          No reschedule times are available for this date.
        </Alert>
      );
    }
    return (
      <>
        <select
          aria-controls={
            timeMenuAnchor ? 'coach-reschedule-time-menu' : undefined
          }
          aria-expanded={Boolean(timeMenuAnchor)}
          aria-label="New time"
          className="w-[137px] cursor-pointer border-0 bg-cement-100 px-3 py-2 font-medium"
          disabled={adminCommandFrozen}
          value={selectedTime}
          onChange={(event) => setSelectedTimeInput(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              setTimeMenuAnchor(event.currentTarget);
            }
          }}
          onMouseDown={(event) => {
            event.preventDefault();
            setTimeMenuAnchor(event.currentTarget);
          }}
        >
          <option value="">Select a time</option>
          {visibleSlots.map((slot) => (
            <option
              key={slot.startsAt}
              className={
                slot.conflictClasses?.length || !slot.available
                  ? 'italic'
                  : undefined
              }
              value={slot.startsAt}
            >
              {`${formatTime(slot.startsAt, timezone)}${
                slotConflictAnnouncement(slot)
                  ? ` - ${slotConflictAnnouncement(slot)}`
                  : ''
              }`}
            </option>
          ))}
        </select>
        <Menu
          anchorEl={timeMenuAnchor}
          id="coach-reschedule-time-menu"
          MenuListProps={{
            'aria-label': 'Available times',
            className: 'p-0',
            dense: true,
            role: 'listbox',
          }}
          open={Boolean(timeMenuAnchor)}
          PaperProps={{ className: 'max-h-56 w-[137px] rounded-none' }}
          onClose={() => setTimeMenuAnchor(null)}
        >
          {visibleSlots.map((slot) => (
            <MenuItem
              key={slot.startsAt}
              aria-label={`${formatTime(slot.startsAt, timezone)}${
                slotConflictAnnouncement(slot)
                  ? `. ${slotConflictAnnouncement(slot)}`
                  : ''
              }`}
              className={`min-h-0 px-3 py-1 ${
                slot.conflictClasses?.length || !slot.available ? 'italic' : ''
              }`}
              disabled={adminCommandFrozen}
              role="option"
              selected={slot.startsAt === selectedTime}
              onClick={() => {
                setSelectedTimeInput(slot.startsAt);
                setTimeMenuAnchor(null);
              }}
            >
              {formatTime(slot.startsAt, timezone)}
            </MenuItem>
          ))}
        </Menu>
      </>
    );
  };

  const onSelectDate = (date: string) => {
    if (adminCommandFrozen) return;
    datePickedRef.current = true;
    setSelectedDate(date);
    setSelectedTimeInput('');
    setTimeMenuAnchor(null);
    setErrorMessage(null);
  };

  const submit = async () => {
    if (
      (!selectedSlot && !exactAdminRetry && !exactNativeRetry) ||
      isAdminCoachCollision ||
      isAdminMemberCollision ||
      adminLifecycle.requiresRescheduleReview ||
      submitInFlightRef.current ||
      operationReviewRequired ||
      upgradeRequired ||
      pendingOperationId ||
      (nativeAppointmentId && !nativeReviewAllowsSubmit) ||
      (!nativeAppointmentId &&
        unavailableSlot &&
        !legacyUnavailableAcknowledged)
    )
      return;
    const selectedStart =
      exactNativeRetry && restoredRetryCommand
        ? restoredRetryCommand.startAt
        : (selectedSlot?.startsAt ??
          (adminLifecycle.command.command === 'reschedule'
            ? adminLifecycle.command.payload.startAt
            : ''));
    submitInFlightRef.current = true;
    setSubmitInFlight(true);
    setPendingOperationId(null);
    setPendingOperationRetryAfterSeconds(null);
    setErrorMessage(null);
    const fingerprint = nativeAppointmentId
      ? null
      : rescheduleMutationFingerprint([
          adminCoachId ? 'admin' : 'coach',
          adminCoachId ?? '',
          session.userId,
          session.id,
          session.recurringId,
          scope,
          session.startedAt,
          selectedStart,
        ]);
    const mutationId = fingerprint ? getRescheduleMutationId(fingerprint) : '';
    try {
      const common = {
        id: session.id,
        mutationId,
        startTime: selectedStart,
        viewerTimezone: timezone,
      };
      if (nativeAppointmentId) {
        if (adminAppointment) {
          const adminResult = await adminLifecycle.submit();
          releaseSubmit();
          if (!adminResult || adminResult.status === 'pending') return;
          if (adminResult.status === 'rejected') {
            setErrorMessage(
              adminResult.rejection?.message ??
                'Unable to reschedule this session. Please try again.',
            );
            return;
          }
          const confirmedStart =
            adminResult.appointments.find(
              ({ id }) => id === adminAppointment.id,
            )?.currentSchedule.startAt ?? selectedStart;
          setConfirmation(
            `Appointment rescheduled to ${formatDateTime(confirmedStart, timezone)}.`,
          );
          return;
        }
        const retrySubmission =
          exactNativeRetry &&
          restoredRetryCommand &&
          lifecycle.restoredSubmitting
            ? {
                bytes: lifecycle.restoredSubmitting.bytes,
                command: restoredRetryCommand,
              }
            : null;
        let reviewedCommand: NativeRescheduleRetryCommand;
        let bytes: string;
        if (retrySubmission) {
          reviewedCommand = retrySubmission.command;
          bytes = retrySubmission.bytes;
        } else {
          const target = nativeContext?.rescheduleTarget;
          const appliesToFutureSeries = scope === 'future_series';
          const preflight = await lifecycle.read();
          if (preflight.kind !== 'ready') {
            if (preflight.kind !== 'upgrade') await nativeQuery.refetch();
            releaseSubmit();
            if (preflight.kind === 'upgrade') setUpgradeRequired(true);
            else
              setErrorMessage(
                'Appointment details changed. Review the refreshed session and confirm again.',
              );
            return;
          }
          const appointment = preflight.preimage;
          if (
            !target ||
            target.id !== nativeAppointmentId ||
            (appliesToFutureSeries && !canApplyToFutureSeries)
          ) {
            await nativeQuery.refetch();
            releaseSubmit();
            setErrorMessage(
              'Appointment details changed. Review the refreshed session and confirm again.',
            );
            return;
          }
          if (!authoritativeReview || !reviewMatchesControls) {
            releaseSubmit();
            setErrorMessage(
              'The selected schedule has not completed authoritative review.',
            );
            return;
          }
          reviewedCommand = {
            appointmentId: nativeAppointmentId,
            busyConflictsAcknowledged,
            expectedVersion: appointment.version,
            nearTermAcknowledged,
            reviewDigest: authoritativeReview.reviewDigest,
            scope: appliesToFutureSeries ? 'this_and_following' : 'appointment',
            startAt: selectedStart,
            viewerTimezone: timezone,
            ...appointmentSeriesCommandFields(
              appointmentCapability,
              appointment,
            ),
          };
          bytes = canonicalAppointmentCommand(reviewedCommand);
        }
        if (lifecycle.submissionBlocked(bytes)) {
          releaseSubmit();
          setErrorMessage(
            'This rejected reschedule is unchanged. Review the refreshed session and choose a different time before saving again.',
          );
          return;
        }
        const submission = lifecycle.submissionFor(bytes, exactNativeRetry);
        if (submission.kind === 'review') {
          releaseSubmit();
          setErrorMessage(
            submission.reason === 'changed-command'
              ? 'The changed request cannot replace the saved reschedule. Restore the original time or contact support.'
              : 'The previous reschedule outcome is unknown. Use the explicit unchanged retry or contact support.',
          );
          return;
        }
        const { idempotencyKey } = submission;
        if (!lifecycle.recordSubmitting({ bytes, idempotencyKey })) {
          releaseSubmit();
          setErrorMessage(
            'We could not safely save this reschedule. Please enable session storage and try again.',
          );
          return;
        }
        const nativeResult = await rescheduleCoachAppointmentV2({
          appointmentId: reviewedCommand.appointmentId,
          busyConflictsAcknowledged: reviewedCommand.busyConflictsAcknowledged,
          expectedSeriesMutationVersion:
            reviewedCommand.expectedSeriesMutationVersion ?? 0,
          expectedVersion: reviewedCommand.expectedVersion,
          idempotencyKey,
          nearTermAcknowledged: reviewedCommand.nearTermAcknowledged,
          proposedStartAt: reviewedCommand.startAt,
          reviewDigest: reviewedCommand.reviewDigest,
          scope: reviewedCommand.scope,
          seriesId: reviewedCommand.seriesId,
          viewerTimezone: reviewedCommand.viewerTimezone,
        }).unwrap();
        if (
          nativeResult.status === 'pending' ||
          nativeResult.status === 'recovery_required'
        ) {
          setUpgradeRequired(false);
          lifecycle.recordPending({
            bytes,
            idempotencyKey,
            operationId: nativeResult.operationId,
            retryAfterSeconds: nativeResult.retryAfterSeconds ?? null,
          });
          setPendingOperationId(nativeResult.operationId);
          setPendingOperationRetryAfterSeconds(
            nativeResult.retryAfterSeconds ?? null,
          );
          releaseSubmit();
          return;
        }
        if (nativeResult.status === 'rejected') {
          await rereadAfterRejection({
            bytes,
            idempotencyKey,
            operationId: nativeResult.operationId,
          });
          setErrorMessage(
            nativeResult.reason ??
              'Unable to reschedule this session. Please try again.',
          );
          releaseSubmit();
          return;
        }
        if (nativeResult.status === 'reconciliation_required') {
          recordReconciliationRequired({
            bytes,
            idempotencyKey,
            operationId: nativeResult.operationId,
          });
          setOperationReviewRequired(true);
          setErrorMessage(
            nativeResult.reason ??
              'This reschedule requires Ops reconciliation before another change.',
          );
          releaseSubmit();
          return;
        }
        const confirmedSchedule = nativeResult.appointments.find(
          ({ id }) => id === nativeAppointmentId,
        )?.currentSchedule;
        if (!confirmedSchedule) {
          setOperationReviewRequired(true);
          setPendingOperationId(null);
          setPendingOperationRetryAfterSeconds(null);
          releaseSubmit();
          setErrorMessage(
            'The confirmed operation does not match this appointment. The reschedule remains saved for review.',
          );
          return;
        }
        const terminalConfirmation = nativeTerminalConfirmationText(
          reviewedCommand.scope,
          confirmedSchedule,
          nativeResult.summary,
          timezone,
        );
        if (!terminalConfirmation) {
          setOperationReviewRequired(true);
          setPendingOperationId(null);
          setPendingOperationRetryAfterSeconds(null);
          releaseSubmit();
          setErrorMessage(
            'The confirmed series operation did not include its authoritative summary. The reschedule remains saved for review.',
          );
          return;
        }
        if (fingerprint) clearRescheduleMutationId(fingerprint);
        const handled = recordConfirmed();
        releaseSubmit();
        if (!handled) return;
        setConfirmation(terminalConfirmation);
      } else if (!adminCoachId) {
        const result: RescheduleSessionResponse = await coachRescheduleSession({
          ...common,
          overrideUnavailable: Boolean(
            unavailableSlot && legacyUnavailableAcknowledged,
          ),
          scope,
        }).unwrap();
        setConfirmation(confirmationText(result, timezone, scope));
      } else if (session.isChemistryCall) {
        const result: Session = await adminChemistryRescheduleSession({
          ...common,
          coachId: adminCoachId,
          memberId: session.userId,
        }).unwrap();
        setConfirmation(
          `Session rescheduled to ${formatDateTime(result.startedAt, timezone)}.`,
        );
      } else {
        const result: RescheduleSessionResponse = await adminRescheduleSession({
          ...common,
          coachId: adminCoachId,
          memberId: session.userId,
          overrideUnavailable: Boolean(
            unavailableSlot && legacyUnavailableAcknowledged,
          ),
          scope,
        }).unwrap();
        setConfirmation(confirmationText(result, timezone, scope));
      }
      releaseSubmit();
      if (fingerprint) clearRescheduleMutationId(fingerprint);
    } catch (error) {
      const upgradeGuardSpent = appointmentUpgradeGuardSpent(error);
      const upgradeRetainsOwnership =
        upgradeGuardSpent ||
        (error as { upgrade?: { state?: unknown } }).upgrade?.state ===
          'reload-requested';
      if (upgradeGuardSpent) setUpgradeRequired(true);
      if (
        nativeAppointmentId &&
        !upgradeRetainsOwnership &&
        !shouldReconcileAppointmentWrite(error)
      ) {
        clearOwnedOperation();
      }
      if (isNonRetryableClientError(error)) {
        if (fingerprint) clearRescheduleMutationId(fingerprint);
      }
      releaseSubmit();
      throw error;
    }
  };

  const memberSecondary = [
    session.user.company?.name,
    seatLabel(session.user.profile?.seatType),
  ]
    .filter(Boolean)
    .join(' · ');

  let content = null;
  let footer = null;
  if (confirmation) {
    content = (
      <div className="flex flex-col gap-4">
        <CoachSessionParticipants
          memberSecondary={memberSecondary}
          session={session}
        />
        <div className="rounded-lg border border-cement-200 px-3 py-4 text-cement-400 line-through">
          {displayedSummary}
        </div>
        <Typography>
          {scope === 'future_series'
            ? 'Future sessions have been rescheduled to:'
            : 'has been rescheduled to:'}
        </Typography>
        <div
          className="rounded-lg border border-cement-200 px-3 py-4 text-sage-500"
          data-testid="reschedule-confirmed-time"
        >
          {confirmation}
        </div>
      </div>
    );
    footer = (
      <Button variant="contained" onClick={requestClose}>
        OK
      </Button>
    );
  } else if (isLoading) {
    content = <CircularProgress className="mt-6" />;
  } else if (isError && !data) {
    content = (
      <Alert className="mt-6" severity="error">
        Unable to load reschedule options. Please try again.
      </Alert>
    );
  } else {
    content = (
      <div className="flex flex-col gap-4">
        {isAdminCoachCollision && (
          <Alert severity="warning">
            This reschedule is unavailable because you are signed in as the
            selected coach. Use the coach Schedule instead.
          </Alert>
        )}
        {isAdminMemberCollision && (
          <Alert severity="warning">
            An admin cannot reschedule an Appointment where they are the
            selected member.
          </Alert>
        )}
        {canDisplayFutureSeries && (
          <FormControlLabel
            control={
              <Checkbox
                checked={scope === 'future_series'}
                disabled={adminCommandFrozen}
                onChange={(event) => {
                  setSelectedTimeInput('');
                  setErrorMessage(null);
                  setScope(event.target.checked ? 'future_series' : 'single');
                }}
              />
            }
            label="Apply to all future sessions in this series"
          />
        )}

        <CoachSessionParticipants
          memberSecondary={memberSecondary}
          session={session}
        />

        {scope === 'future_series' ? (
          <div
            className="rounded-lg border border-cement-200 px-4 py-3 text-cement-400 line-through"
            data-testid="reschedule-series-context"
          >
            <Typography className="text-sm font-medium">
              Current series
            </Typography>
            <Typography className="text-base">{displayedSummary}</Typography>
          </div>
        ) : (
          <div className="rounded-lg border border-cement-200 px-4 py-3">
            <Typography className="text-sm font-medium">
              Original time
            </Typography>
            <Typography className="text-base text-cement-400 line-through">
              {occurrenceSummary}
            </Typography>
          </div>
        )}

        <div>
          <Typography className="text-base font-medium">
            Choose a new date and time
          </Typography>
          <Typography className="text-xs text-cement-400" variant="caption">
            {`Time zone shown in ${timezone}`}
          </Typography>
        </div>

        <div
          className="flex flex-col overflow-visible rounded-[22px] border border-cement-200 sm:flex-row"
          data-testid="reschedule-picker-panel"
        >
          <div className="min-w-0 flex-1 p-4 sm:basis-[258px]">
            <MentoCalDatePicker
              days={
                adminCommandFrozen
                  ? pickerDays.map((day) => ({ ...day, enabled: false }))
                  : pickerDays
              }
              selectedDate={selectedDate}
              todayIso={todayIso}
              onNextMonth={
                (isAdminChemistry && !nativeAppointmentId) || adminCommandFrozen
                  ? undefined
                  : () => {
                      onSelectDate(shiftMonth(selectedDate, 1));
                    }
              }
              onPrevMonth={
                (isAdminChemistry && !nativeAppointmentId) ||
                isCurrentOrEarlierMonth ||
                adminCommandFrozen
                  ? undefined
                  : () => {
                      onSelectDate(shiftMonth(selectedDate, -1));
                    }
              }
              onSelectDate={adminCommandFrozen ? () => undefined : onSelectDate}
            />
          </div>

          <div className="flex min-w-0 flex-1 flex-col gap-2 border-t border-cement-200 p-2 sm:basis-[213px] sm:border-l sm:border-t-0">
            <Typography className="text-base font-medium">
              {formatSelectedDayLabel(selectedDate)}
            </Typography>
            <Typography
              className="text-xs italic text-cement-400"
              variant="caption"
            >
              Italicized times have a conflict. Most can be overridden once
              selected; a few cannot, and say so.
            </Typography>

            {renderTimeOptions()}
          </div>
        </div>

        {unavailableSlot && !nativeAppointmentId && (
          <div
            className="flex flex-col gap-1"
            data-testid="coach-reschedule-conflict"
          >
            <Typography className="text-sm text-bloom-500">
              There is a conflict with Mento Availability.
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={legacyUnavailableAcknowledged}
                  onChange={(event) =>
                    setLegacyUnavailableAcknowledged(event.target.checked)
                  }
                />
              }
              label="I understand this time is unavailable and want to use it"
            />
          </div>
        )}
        {selectedSlot && isWithin48Hours && (
          <div className="flex flex-col gap-3">
            <Typography className="text-sm text-bloom-500">
              This session is less than 48 hours away.
            </Typography>
            <Typography className="text-sm">Continue anyway?</Typography>
          </div>
        )}
        {nativeAppointmentId &&
          selectedTime &&
          (adminAppointment ? adminReviewMutation : reviewMutation)
            .isLoading && (
            <div aria-live="polite" role="status">
              Reviewing the selected schedule...
            </div>
          )}
        {nativeAppointmentId &&
          authoritativeReview &&
          reviewMatchesControls &&
          !adminCommandFrozen && (
            <div
              className="flex flex-col gap-3"
              data-testid="coach-reschedule-review"
            >
              <Typography className="font-medium">
                {authoritativeReview.summary.affectedCount === 1
                  ? `1 appointment will move to ${formatDateTime(authoritativeReview.proposedSchedule.startAt, timezone)}.`
                  : `${authoritativeReview.summary.affectedCount} appointments from ${formatDate(authoritativeReview.summary.firstDate)} through ${formatDate(authoritativeReview.summary.lastDate)} will move to ${authoritativeReview.summary.proposedWeekday} at ${authoritativeReview.summary.proposedTime} ${authoritativeReview.summary.timezone}.`}
              </Typography>
              {authoritativeReview.scope !== 'appointment' && (
                <Typography className="text-sm text-cement-500">
                  Cadence:{' '}
                  {formatCadence(
                    authoritativeReview.summary.cadenceWeeks,
                    authoritativeReview.summary.proposedWeekday,
                  )}
                </Typography>
              )}
              {authoritativeReview.conflicts.map((conflict) => (
                <Alert
                  key={conflict.class}
                  severity={
                    conflict.disposition === 'blocking' ? 'error' : 'warning'
                  }
                >
                  <strong>{conflictClassCopy(conflict.class)}</strong>
                  {` Affected dates: ${conflict.affectedDates.join(', ')} (${authoritativeReview.summary.timezone}).`}
                  {conflict.maskedAccountLabels.length > 0
                    ? ` Accounts: ${conflict.maskedAccountLabels.join(', ')}.`
                    : ''}
                </Alert>
              ))}
              {overrideAcknowledgementRequired && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={busyConflictsAcknowledged}
                      inputProps={{
                        'aria-describedby': 'conflict-acknowledgement-help',
                      }}
                      onChange={(event) =>
                        setBusyConflictsAcknowledged(event.target.checked)
                      }
                    />
                  }
                  label="I understand the conflicts above and want to use this time"
                />
              )}
              {overrideAcknowledgementRequired && (
                <Typography
                  id="conflict-acknowledgement-help"
                  variant="caption"
                >
                  Past times and times we could not check cannot be overridden.
                </Typography>
              )}
              {nearTermAcknowledgementRequired && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={nearTermAcknowledged}
                      inputProps={{
                        'aria-describedby': 'near-term-acknowledgement-help',
                      }}
                      onChange={(event) =>
                        setNearTermAcknowledged(event.target.checked)
                      }
                    />
                  }
                  label="I understand this appointment starts within 48 hours"
                />
              )}
              {nearTermAcknowledgementRequired && (
                <Typography
                  id="near-term-acknowledgement-help"
                  variant="caption"
                >
                  The member may have limited notice of this change.
                </Typography>
              )}
            </div>
          )}
        {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
        {lifecycle.reviewed && (
          <Alert severity="warning">
            Refreshed appointment state: version {lifecycle.reviewed.version},{' '}
            series {lifecycle.reviewed.seriesId ?? 'standalone'}, fence{' '}
            {lifecycle.reviewed.seriesMutationVersion}.
          </Alert>
        )}
        {lifecycle.reviewUnavailable && (
          <Alert severity="warning">
            Authoritative appointment state is unavailable. Nothing was
            submitted; review and reconfirm to try another detail read.
          </Alert>
        )}
        {lifecycle.rejected && (
          <Alert severity="warning">
            Rejected operation {lifecycle.rejected.operationId} is retained for
            review.
          </Alert>
        )}
        {lifecycle.restoredSubmitting && !submitInFlightRef.current && (
          <Alert severity="info">
            {/* The Save/retry control is rendered unconditionally in this
                drawer's footer below, so a retry is always available here. */}
            {appointmentRestoredSubmittingMessage('reschedule', {
              retryAvailable: true,
            })}
          </Alert>
        )}
        {pendingOperationId && (
          <Alert severity="info">
            {appointmentPendingMessage('reschedule')}
          </Alert>
        )}
        {adminLifecycle.terminalHydrationForbidden && (
          <Alert severity="warning">
            {PLATFORM_ADMIN_TERMINAL_HYDRATION_FORBIDDEN_MESSAGE}
          </Alert>
        )}
        {!adminLifecycle.terminalHydrationForbidden &&
          adminLifecycle.record?.phase === 'pending' && (
            <Alert severity="info">
              The admin reschedule is still pending.
            </Alert>
          )}
        {adminLifecycle.record?.phase === 'ambiguous' &&
          !adminLifecycle.requiresRescheduleReview && (
            <Alert severity="warning">
              The previous admin reschedule outcome is unknown. Retry uses the
              exact saved request.
            </Alert>
          )}
        {adminLifecycle.requiresRescheduleReview && (
          <Alert severity="warning">
            {PLATFORM_ADMIN_RESCHEDULE_REVIEW_REQUIRED_MESSAGE}
          </Alert>
        )}
        {adminManualReview && (
          <Alert severity="warning">
            {PLATFORM_ADMIN_MANUAL_REVIEW_MESSAGE}
          </Alert>
        )}
        {upgradeRequired && (
          <Alert severity="error">
            Appointment client upgrade required. This operation remains saved
            for review; reload or contact support before continuing.
          </Alert>
        )}
      </div>
    );
    footer = (
      <>
        <Button
          disabled={Boolean(pendingOperationId)}
          variant="text"
          onClick={requestClose}
        >
          Cancel
        </Button>
        <Button
          aria-label="Save reschedule"
          disabled={
            !canSubmit ||
            lifecycle.isReading ||
            adminManualReview ||
            adminRejected
          }
          variant="contained"
          onClick={() => {
            setErrorMessage(null);
            submit().catch((error: unknown) => {
              setErrorMessage(
                `Unable to reschedule this session. Please try again.${rescheduleFailureDetail(
                  error,
                )}`,
              );
            });
          }}
        >
          {(lifecycle.restoredSubmitting && !submitInFlightRef.current) ||
          adminLifecycle.record?.phase === 'ambiguous'
            ? APPOINTMENT_RETRY_BUTTON_LABEL
            : 'Save'}
        </Button>
        {adminRejected && (
          <Button variant="outlined" onClick={startNewAdminAttempt}>
            Start new attempt
          </Button>
        )}
      </>
    );
  }

  return (
    <Drawer
      anchor="right"
      open={RESCHEDULE_DRAWER_OPEN}
      PaperProps={{
        'aria-labelledby': 'coach-reschedule-title',
        role: 'dialog',
      }}
      onClose={requestClose}
    >
      <div
        className="flex h-full max-w-full flex-col"
        data-action-source={actionSubject?.source}
        data-testid="coach-reschedule-drawer"
        style={{ width: 'min(532px, 100vw)' }}
      >
        <header className="flex shrink-0 items-center justify-between px-[30px] pb-1 pt-8">
          <Typography id="coach-reschedule-title" variant="h4">
            {confirmation ? 'Confirmed!' : 'Reschedule'}
          </Typography>
          <IconButton
            aria-label="Close"
            className="p-1"
            disabled={Boolean(pendingOperationId)}
            onClick={requestClose}
          >
            <Icon>close</Icon>
          </IconButton>
        </header>
        <main className="min-h-0 flex-1 overflow-y-auto px-[30px] pb-4 pt-1">
          {content}
        </main>
        {footer && (
          <footer className="flex shrink-0 items-center justify-end gap-2 border-t border-cement-200 px-[14px] py-4">
            {footer}
          </footer>
        )}
      </div>
    </Drawer>
  );
};

export default CoachRescheduleSession;
